/**
 * Formato COTIZACIÓN (skill impresion-fiscal): carta, SOLO precios en Bs por línea, SIN columna de código,
 * totales al pie en Bs y $BCV, vigencia y tasa del día visibles. PDF: imprimir → "Guardar como PDF".
 * Cada combo o receta lleva una cabecera con su nombre y su medida, y su descuento dice «Desc. combo» (spec 20).
 */
import { Money } from '@mg/shared';
import { Fragment, useEffect } from 'react';
import { useParams } from 'react-router';
import { Leyendas, Membrete, useEmpresaImpresion } from '@/componentes/Impresion.tsx';
import { useRegistroImpresion } from '@/lib/impresion.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { cabeceraDeCombo, tituloCombo } from '@/modulos/combos/documento.ts';
import { registrarImpresionCotizacion, useCotizacion } from './api.ts';

const registrar = (id: string) => registrarImpresionCotizacion(id).then((r) => r.copia);

export function ImprimirCotizacion() {
  const { id } = useParams<{ id: string }>();
  const cot = useCotizacion(id ?? null);
  const empresa = useEmpresaImpresion();
  const copia = useRegistroImpresion(id, registrar);
  useEffect(() => {
    if (cot.data && empresa.data && copia !== null) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [cot.data, empresa.data, copia]);
  if (!cot.data || !empresa.data || copia === null) return <p style={{ padding: 24, fontFamily: 'Georgia, serif' }}>Preparando cotización…</p>;
  const c = cot.data;
  const e = empresa.data;
  // Descuentos a la vista (mismo criterio de la factura): columna «% Dscto» si algún renglón lo lleva y, si hay
  // descuento global, Sub Total y Descuento antes de la base. Así el papel cuadra a mano
  const conDescuento = c.lineas.some((l) => Money.parse(l.descuento_pct).esPositivo());
  const subtotalBs = Money.sumar(c.lineas.map((l) => Money.parse(l.subtotal_bs)));
  // Descuento global en Bs: lo que separa la suma de los renglones (ya con su descuento) de la base
  const descuentoBs = Money.parse(c.descuento_pct).esPositivo() ? subtotalBs.sub(Money.parse(c.base_bs)).redondear2() : Money.cero();
  // Totales también en $ a tasa BCV (Bs ÷ BCV). El IVA y el descuento en $ salen por diferencia: la columna cuadra a mano
  const aBcv = (bs: Money) => bs.bsAUsd(c.tasa_bcv).redondear2();
  const baseBcv = aBcv(Money.parse(c.base_bs));
  const totalBcv = aBcv(Money.parse(c.total_bs));
  const subtotalBcv = aBcv(subtotalBs);
  return (
    <div className="formato" data-testid="formato-cotizacion" data-formato="carta">
      <style>{`
        @page { size: letter; margin: 12mm; }
        html, body { background: #fff; margin: 0; }
        .formato { font-family: Georgia, 'Times New Roman', serif; color: #000; font-size: 11.5px; width: 7.8in; margin: 0 auto; padding: 6px; position: relative; }
        .formato table { width: 100%; border-collapse: collapse; }
        .formato th, .formato td { padding: 3px 4px; }
        .formato th { border-bottom: 1px solid #000; text-align: left; font-weight: bold; }
        .formato .num { text-align: right; font-variant-numeric: tabular-nums; }
        .formato td.combo { font-weight: bold; padding-top: 6px; }
        .formato .cab { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 6px; }
        .formato .caja { border: 1px solid #000; padding: 4px 6px; }
        .formato .totales { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; border-top: 1px solid #000; padding-top: 2px; }
        .formato .totales table { width: auto; }
        .formato .totales th { border-bottom: 0; text-align: right; font-size: 10px; }
        .formato .totales td { white-space: nowrap; }
        .formato .totales .fin td { border-top: 1px solid #000; font-weight: bold; font-size: 13px; }
        .formato .legal { font-size: 9.5px; margin-top: 8px; }
        @media screen { body { background: #e5e7eb; } .formato { background: #fff; box-shadow: 0 0 8px rgba(0,0,0,.2); margin: 16px auto; } }
      `}</style>
      {/* Membrete con los dos logos (respuesta de la clienta 2026-10-09) */}
      <Membrete empresa={e} sede={e.sucursal ?? { nombre: c.sucursal_nombre, direccion_fiscal: e.direccion_fiscal, telefono: null }} />
      {/* Cliente a la izquierda y número de la cotización a la derecha, en la misma franja */}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, marginBottom: 6 }}>
        <div className="caja" style={{ flex: 1 }}>
          <b>Cliente:</b> {c.cliente_razon_social} · <b>RIF/CI:</b> {c.cliente_rif}
          {c.cliente_direccion_fiscal ? <><br /><b>Dirección:</b> {c.cliente_direccion_fiscal}</> : null}
        </div>
        <div className="caja" style={{ minWidth: 200, textAlign: 'right' }}>
          <div style={{ fontWeight: 'bold', fontSize: 14 }}>COTIZACIÓN</div>
          <div>N° <b data-testid="formato-numero">{c.numero_formateado}</b> · versión {c.version}</div>
          <div>{formatearFechaHora(c.actualizado_en)}</div>
          {c.vence_en ? <div data-testid="formato-vigencia">Válida hasta <b>{formatearFechaHora(c.vence_en, { hour: undefined, minute: undefined })}</b> ({c.vigencia_dias} días)</div> : null}
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Descripción</th>
            <th className="num">Cant.</th>
            <th className="num">Precio Bs</th>
            {conDescuento ? <th className="num" data-testid="formato-col-descuento">% Dscto</th> : null}
            <th className="num">Total Bs</th>
          </tr>
        </thead>
        <tbody>
          {c.lineas.map((l, i) => {
            // Combo o receta (spec 20, regla 7): su nombre y la medida antes de su primer renglón
            const combo = cabeceraDeCombo(c.combos, c.lineas, i);
            return (
              <Fragment key={l.id}>
                {combo ? (
                  <tr data-testid="formato-combo">
                    <td colSpan={conDescuento ? 5 : 4} className="combo">{tituloCombo(combo)}</td>
                  </tr>
                ) : null}
                <tr data-testid="formato-renglon">
                  <td>{l.producto_nombre} ({l.presentacion_nombre} × {Money.parse(l.unidades_presentacion).toDisplay()} {l.producto_unidad})</td>
                  <td className="num">{Money.parse(l.cantidad).toDisplay()}</td>
                  <td className="num">{Money.parse(l.precio_unitario_bs).mul(l.unidades_presentacion).redondear2().toDisplay()}</td>
                  {conDescuento ? (
                    <td className="num" data-testid="formato-linea-descuento">
                      {/* El descuento del combo dice que lo es: solo vale si se compra el combo completo */}
                      {l.descuento_combo ? <span style={{ fontSize: 9.5 }}>Desc. combo </span> : null}
                      {Money.parse(l.descuento_pct).esPositivo() ? Money.parse(l.descuento_pct).toDisplay() : ''}
                    </td>
                  ) : null}
                  <td className="num" data-testid="formato-linea-total">{Money.parse(l.subtotal_bs).toDisplay()}</td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {/* Totales en bolívares y, al lado, su equivalente en $ a tasa BCV, con el mismo peso (formato que la clienta ya usa) */}
      <div className="totales">
        <div className="legal" style={{ flex: 1, marginTop: 4 }}>{c.observaciones ? c.observaciones : null}</div>
        <table data-testid="formato-totales">
          <thead>
            <tr>
              <th />
              <th>Bs</th>
              <th>$ a tasa BCV</th>
            </tr>
          </thead>
          <tbody>
            {descuentoBs.esPositivo() ? (
              <>
                <tr>
                  <td className="num">Sub Total</td>
                  <td className="num" data-testid="formato-subtotal-bs">Bs {subtotalBs.toDisplay()}</td>
                  <td className="num">$ {subtotalBcv.toDisplay()}</td>
                </tr>
                <tr>
                  <td className="num">Descuento {Money.parse(c.descuento_pct).toString()}%</td>
                  <td className="num" data-testid="formato-descuento-bs">−Bs {descuentoBs.toDisplay()}</td>
                  <td className="num">−$ {subtotalBcv.sub(baseBcv).toDisplay()}</td>
                </tr>
              </>
            ) : null}
            <tr>
              <td className="num">Base imponible</td>
              <td className="num" data-testid="formato-base-bs">Bs {Money.parse(c.base_bs).toDisplay()}</td>
              <td className="num" data-testid="formato-base-usd">$ {baseBcv.toDisplay()}</td>
            </tr>
            <tr>
              <td className="num">IVA {c.iva_pct}%</td>
              <td className="num" data-testid="formato-iva-bs">Bs {Money.parse(c.iva_bs).toDisplay()}</td>
              <td className="num" data-testid="formato-iva-usd">$ {totalBcv.sub(baseBcv).toDisplay()}</td>
            </tr>
            <tr className="fin">
              <td className="num">TOTAL</td>
              <td className="num" data-testid="formato-total-bs">Bs {Money.parse(c.total_bs).toDisplay()}</td>
              <td className="num" data-testid="formato-total-usd">$ {totalBcv.toDisplay()}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Referencial para pagos en divisas (observación del cliente 24-09-2026): con IVA y sin IVA (base), en $ reales */}
      <div className="legal" style={{ textAlign: 'right' }} data-testid="formato-referencia-divisas">
        Referencial para pagos en divisas: <b>$ {Money.parse(c.total).toDisplay()}</b> con IVA · <b>$ {Money.parse(c.base).toDisplay()}</b> sin IVA (base)
      </div>
      {/* Leyendas (respuesta de la clienta 2026-10-09): en la cotización van todas; el texto se edita en Configuración */}
      <Leyendas textos={[e.leyendas.precios, e.leyendas.igtf, e.leyendas.cambios, e.leyendas.cortes, e.leyendas.retiro]} />
      <div className="legal" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <span>Elaborada por: {c.creado_por_nombre}</span>
        <span>Conforme cliente: ______________________</span>
      </div>
    </div>
  );
}
